import { useState, useEffect } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";

export default function FirstLaunchDisclaimer({ onAccept, onDecline }) {
  const [isOpen, setIsOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Check if user has seen disclaimer
    const hasSeenDisclaimer = localStorage.getItem('disclaimer_accepted');
    if (!hasSeenDisclaimer) {
      // Use setTimeout to avoid setState in effect
      setTimeout(() => setIsOpen(true), 0);
    }
  }, []);

  const handleAccept = () => {
    if (!accepted) {
      return;
    }
    localStorage.setItem('disclaimer_accepted', 'true');
    localStorage.setItem('disclaimer_date', new Date().toISOString());
    setIsOpen(false);
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    setIsOpen(false);
    if (onDecline) onDecline();
    // Redirect to login or exit
    window.location.href = '/login';
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-zinc-900 border-red-500/30 max-w-2xl" hideClose>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-3 text-xl">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            IMPORTANT: Investment Disclaimer
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <h3 className="text-red-400 font-bold mb-2">THIS IS NOT FINANCIAL ADVICE</h3>
            <p className="text-zinc-300 text-sm">
              Zero Deficit™: Pulse provides informational and educational tools ONLY. We are NOT licensed financial advisors, brokers, or investment professionals.
            </p>
          </div>

          <div className="space-y-3 text-sm text-zinc-300">
            <div className="flex items-start gap-2">
              <span className="text-red-400 mt-1">⚠️</span>
              <p><strong>AI Recommendations:</strong> All AI-generated stock picks, ratings, and analysis are algorithmic predictions that may be inaccurate or incomplete. They are NOT personalized advice.</p>
            </div>
            
            <div className="flex items-start gap-2">
              <span className="text-red-400 mt-1">⚠️</span>
              <p><strong>Investment Risk:</strong> Investing in stocks involves substantial risk. You may lose some or ALL of your invested capital. Past performance does NOT guarantee future results.</p>
            </div>
            
            <div className="flex items-start gap-2">
              <span className="text-red-400 mt-1">⚠️</span>
              <p><strong>Your Responsibility:</strong> You are solely responsible for your investment decisions. Always conduct your own research and consult a licensed financial advisor before investing.</p>
            </div>
            
            <div className="flex items-start gap-2">
              <span className="text-red-400 mt-1">⚠️</span>
              <p><strong>No Warranties:</strong> We provide NO guarantees about investment outcomes, data accuracy, or AI prediction success. Service is provided "AS IS" without warranties.</p>
            </div>
            
            <div className="flex items-start gap-2">
              <span className="text-red-400 mt-1">⚠️</span>
              <p><strong>Limitation of Liability:</strong> We are NOT liable for any investment losses, missed asset expirations, data errors, or AI inaccuracies.</p>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-amber-300 text-sm">
              <strong>By using this service, you acknowledge:</strong>
            </p>
            <ul className="list-disc pl-5 space-y-1 text-xs text-amber-200 mt-2">
              <li>You understand investing involves risk of loss</li>
              <li>You will consult a financial advisor for personalized advice</li>
              <li>You will verify all data independently</li>
              <li>You agree to our Terms of Service and Privacy Policy</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <div className="flex items-center space-x-2 mb-4 sm:mb-0">
            <Checkbox 
              id="accept-disclaimer" 
              checked={accepted}
              onCheckedChange={setAccepted}
              className="border-zinc-600"
            />
            <label
              htmlFor="accept-disclaimer"
              className="text-sm text-zinc-300 cursor-pointer"
            >
              I understand and accept the risks
            </label>
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1 sm:flex-none border-zinc-700 hover:bg-red-500/10 hover:text-red-400"
            >
              <X className="w-4 h-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!accepted}
              className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4 mr-2" />
              Accept & Continue
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
